import {
  Button,
  Tooltip,
  IconButton,
  Box,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import type { Provider } from '@repo/api-gw-sdk';
import { useMemo, useState } from 'react';

import { useDAL } from '@/contexts/useDAL';
import { isValueCondition } from '@/types/advanceFilter';

import { Loader } from '../../layout/loading';
import { SearchTextBox } from '../../search/searchTextBox';
import { MultiSelectFilter, type FilterProps } from '../filters';

export interface TagsFilterProps extends FilterProps {
  supportedProviders?: Provider[];
  onDoneClick: () => void;
  initialFilterMode: 'key' | 'value' | 'key-value';
  hideButton?: boolean;
  popoverOpen?: boolean;
  anchorRef?: React.RefObject<HTMLElement | null>;
  onClose?: () => void;
}

export const TagsFilter = (props: TagsFilterProps) => {
  const dal = useDAL();
  const [keyFilter, setKeyFilter] = useState('');
  const [valueFilter, setValueFilter] = useState('');

  const [filterMode, setFilterMode] = useState<'key' | 'value' | 'key-value'>(
    props.initialFilterMode
  );
  const [isScrolledVertically, setScrolledVertically] = useState(false);
  const { data: body, isLoading } = dal.inventory.tags.useList();

  const condition = props.conditions.find(
    (x) => isValueCondition(x) && x.property === props.property.name
  );

  const selectedResources = useMemo(
    () => (isValueCondition(condition) && condition?.value) || [],
    [condition]
  );

  const allTags = useMemo(() => {
    const tags = body?.tags || [];

    selectedResources.forEach((tag) => {
      const [key, value] = tag.split('=');
      if (!tags.some((t) => t.key === key && t.value === value)) {
        tags.push({ key, value });
      }
    });

    return tags;
  }, [body, selectedResources]);

  const tagsReduced = useMemo(
    () =>
      allTags.reduce(
        (acc, tag) => {
          {
            acc.keys.add(tag.key);
            if (tag.value) {
              acc.values.add(tag.value);
            }
            return acc;
          }
        },
        { keys: new Set<string>(), values: new Set<string>() }
      ),
    [allTags]
  );

  const keys = useMemo(
    () =>
      Array.from(tagsReduced.keys)
        .filter(
          (key) =>
            !keyFilter || key.toLowerCase().includes(keyFilter.toLowerCase())
        )
        .map((key) => ({
          id: key,
          name: key,
        })),
    [keyFilter, tagsReduced.keys]
  );

  const values = useMemo(
    () =>
      Array.from(tagsReduced.values)
        .filter(
          (value) =>
            !valueFilter ||
            value.toLowerCase().includes(valueFilter.toLowerCase())
        )
        .sort()
        .map((value) => ({
          id: value,
          name: value,
        })),
    [valueFilter, tagsReduced.values]
  );

  const keyValuePairs = useMemo(() => {
    const allValues = Array.from(
      new Set(
        allTags
          .filter(
            (t) =>
              (!keyFilter ||
                t.key.toLowerCase().includes(keyFilter.toLowerCase())) &&
              (!valueFilter ||
                !!t.value?.toLowerCase().includes(valueFilter.toLowerCase()))
          )
          .map((x) => `${x.key}=${x.value || ''}`)
      )
    );

    return allValues.map((value) => ({
      id: value,
      name: value,
    }));
  }, [allTags, keyFilter, valueFilter]);

  if (isLoading) {
    return <Loader />;
  }

  const options =
    filterMode === 'key'
      ? keys
      : filterMode === 'value'
        ? values
        : keyValuePairs;

  return (
    <Stack className='h-full overflow-hidden'>
      <Stack
        gap='12px'
        className='px-[24px] pb-[24px] py-[12px]'
        sx={{
          borderBottom: '1px solid var(--mui-palette-divider)',
          boxShadow: isScrolledVertically
            ? 'var(--mui-customShadows-panel)'
            : undefined,
        }}
      >
        <Typography>Filter by</Typography>
        <Select
          value={filterMode}
          onChange={(event) => setFilterMode(event.target.value as never)}
        >
          <MenuItem value='key'>Tag keys</MenuItem>
          {/* <MenuItem value='value'>Tag values</MenuItem> */}
          <MenuItem value='key-value'>Key-value pairs</MenuItem>
        </Select>
        <Typography className='my-[12px]' variant='body1'>
          {`To filter your resources, select one or more ${filterMode}${filterMode === 'key' || filterMode === 'value' ? 's' : ' pairs'}.`}
        </Typography>
        {(filterMode.includes('key') || filterMode.includes('value')) && (
          <Stack
            direction='row'
            alignItems='center'
            gap='12px'
            className='w-full'
          >
            <Stack gap='12px' flexGrow={1}>
              {filterMode.includes('key') && (
                <SearchTextBox
                  value={keyFilter}
                  onChange={setKeyFilter}
                  placeholder='Search by key'
                />
              )}
              {filterMode.includes('value') && (
                <SearchTextBox
                  value={valueFilter}
                  onChange={setValueFilter}
                  placeholder='Search by value'
                />
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
      <Box
        flexGrow={1}
        className='overflow-auto px-[24px] py-[12px]'
        onScroll={(event) => {
          const target = event.target as HTMLDivElement;
          if (typeof target.scrollTop === 'number') {
            const isScrolled = target.scrollTop > 0;
            if (isScrolled !== isScrolledVertically) {
              setScrolledVertically(isScrolled);
            }
          }
        }}
      >
        {options.length === 0 && (
          <Typography variant='body1' className='my-[12px]'>
            {`No matching values found. Try a new search term.`}
          </Typography>
        )}
        {options.length > 0 && (
          <MultiSelectFilter
            {...props}
            showSelectAll={!!keyFilter || !!valueFilter}
            options={options}
          />
        )}
      </Box>
      {selectedResources.length > 0 && (
        <Stack
          direction='row'
          alignItems='center'
          className='px-[24px] py-[16px]'
          justifyContent='space-between'
          sx={{
            boxShadow: 'var(--mui-customShadows-panelUp)',
          }}
        >
          <Typography>{`${selectedResources.length} ${filterMode === 'key' || filterMode === 'value' ? filterMode : 'pair'}${selectedResources.length === 1 ? '' : 's'} selected`}</Typography>
          <Stack direction='row' alignItems='center'>
            <Tooltip title='Clear all'>
              <IconButton
                onClick={() =>
                  props.onChange(
                    props.conditions.filter((x) => x !== condition)
                  )
                }
              >
                <i className='material-symbols-filter-alt-off-outline-rounded text-[var(--mui-palette-text-primary)] h-[20px] w-[20px]'></i>
              </IconButton>
            </Tooltip>
            <Button
              className='ml-[12px]'
              variant='outlined'
              onClick={() => props.onDoneClick()}
            >
              Done
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
